/**
 * FieldHelpIcon Component (Feature #55)
 *
 * Small "?" icon next to field labels that shows AI-generated help tooltips.
 * On hover: show cached tooltip. On click: open chatbot with pre-filled question.
 *
 * @module FieldHelpIcon
 */

'use client';

import React, { useCallback, useState } from 'react';
import { Box, IconButton, Tooltip } from '@axon/ui';
import { LuCircleHelp } from 'react-icons/lu';

import { useFieldHelp } from '../hooks/useFieldHelp';

// ============================================================================
// PROPS
// ============================================================================

export interface FieldHelpIconProps {
  /** Page path (e.g., '/orders') */
  page: string;
  /** Field identifier (e.g., 'status') */
  fieldId: string;
  /** Language preference */
  lang?: 'en' | 'tr';
  /** Size of the icon */
  size?: 'xs' | 'sm';
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FieldHelpIcon({
  page,
  fieldId,
  lang = 'en',
  size = 'xs',
}: FieldHelpIconProps): React.ReactElement {
  const { helpData, isLoading, fetchHelp } = useFieldHelp();
  const [hasFetched, setHasFetched] = useState(false);

  const handleHover = useCallback(async () => {
    if (!hasFetched) {
      setHasFetched(true);
      await fetchHelp(page, fieldId, lang);
    }
  }, [hasFetched, fetchHelp, page, fieldId, lang]);

  const tooltipContent = isLoading ? 'Loading...' : (helpData?.tooltip ?? `Help for ${fieldId}`);

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      marginLeft="1"
      onMouseEnter={handleHover}
      onFocus={handleHover}
    >
      <Tooltip content={tooltipContent} openDelay={200} asChild>
        <IconButton
          aria-label={`Help for ${fieldId}`}
          icon={<LuCircleHelp size={size === 'xs' ? 14 : 16} />}
          size={size}
          variant="ghost"
          touchFriendly={false}
        />
      </Tooltip>
    </Box>
  );
}
